import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Output, signal } from '@angular/core';
import { SelectItem, SelectMenuComponent, SelectMode } from '../../../../shared/components/select-menu/select-menu.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AssetVersionPipe } from '../../../../pipes/asset-version.pipe';
import { POLARIS_NAME } from '../../copilot.model';
import { tooltips } from '../../../../utils/tooltips';
import { DashboardService } from '../../../../services/dashboard.service';
import { StateService } from '../../../../services/state.service';
import { UtilService } from '../../../../services/util.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SpinnerIconComponent } from '../../../../shared/components/icons/spinner-icon/spinner-icon.component';

const MAX_COMPETITORS = 5;

export enum DeckContent {
  OVERVIEW = 'Overview',
  AUDIENCE_RESEARCH = 'Audience Research',
  CREATIVE_ANALYSIS = 'Creative Analysis',
  POLARIS_INSIGHTS = 'POLARIS Insights',
}

const DECK_CONTENT_TO_SERVER: Record<DeckContent, string> = {
  [DeckContent.OVERVIEW]: 'overview',
  [DeckContent.AUDIENCE_RESEARCH]: 'audience_research',
  [DeckContent.CREATIVE_ANALYSIS]: 'creative_analysis',
  [DeckContent.POLARIS_INSIGHTS]: 'polaris_insights',
};

export interface StrategyDeckSubmitEvent {
  brandKey: string;
  competitorKeys: string[];
  deckContent: string[];
  accountKey: string;
}

@Component({
  selector: 'app-strategy-deck',
  imports: [
    ReactiveFormsModule,
    SelectMenuComponent,
    AssetVersionPipe,
    MatTooltipModule,
    SpinnerIconComponent,
  ],
  templateUrl: './strategy-deck.component.html',
  styleUrl: './strategy-deck.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StrategyDeckComponent {

  @Output() exitWizard = new EventEmitter<void>();
  @Output() submitWizard = new EventEmitter<StrategyDeckSubmitEvent>();

  private dashboardService = inject(DashboardService);
  private stateService = inject(StateService);
  private utilService = inject(UtilService);
  protected readonly polarisName = POLARIS_NAME;
  protected readonly tooltips = tooltips.polaris;
  protected readonly SelectMode = SelectMode;
  protected readonly deckContentValues = Object.values(DeckContent);

  protected wizardForm: FormGroup = new FormGroup({
    brands: new FormControl<string | null>(null, [Validators.required]),
    competitors: new FormControl<string[]>([], [Validators.required, Validators.minLength(1), Validators.maxLength(MAX_COMPETITORS)]),
    deckContent: new FormControl<DeckContent[]>([DeckContent.OVERVIEW], [Validators.required, Validators.minLength(1)]),
    dateRange: new FormControl<string | null>(null),
  });

  protected isLoading = signal<boolean>(false);
  protected brandControlValue = toSignal(this.wizardForm.get('brands')!.valueChanges, { initialValue: this.wizardForm.get('brands')!.value });

  protected dateRangeOptions = computed<SelectItem[]>(() => [ { label: 'Last 30 days', key: 'last_30_days' } ]);
  protected brandOptions = computed<SelectItem[]>(() => this.dashboardService.myBrandOptions() || []);
  protected competitorOptions = computed<SelectItem[]>(() => {
    const brandKey = this.brandControlValue();
    return brandKey
      ? this.dashboardService.getAllCompetitorsByBrandKey(brandKey) ?? []
      : [];
  });

  protected showSelectAllOption = computed<boolean>(() => this.competitorOptions().length <= MAX_COMPETITORS);

  protected get brandsControl(): FormControl<string | null> {
    return this.wizardForm.get('brands') as FormControl<string | null>;
  }

  protected get competitorsControl(): FormControl<string[]> {
    return this.wizardForm.get('competitors') as FormControl<string[]>;
  }

  protected get deckContentControl(): FormControl<DeckContent[]> {
    return this.wizardForm.get('deckContent') as FormControl<DeckContent[]>;
  }

  protected get dateRangeControl(): FormControl<string | null> {
    return this.wizardForm.get('dateRange') as FormControl<string | null>;
  }

  protected deckContentClicked(content: DeckContent): void {
    // Overview is always included in the deck
    if (content === DeckContent.OVERVIEW) return;

    const current = this.deckContentControl.value || [];
    const updated = current.includes(content)
      ? current.filter((c: DeckContent) => c !== content)
      : [...current, content];
    this.deckContentControl.setValue(updated);
  }

  protected get isFormValid(): boolean {
    return this.wizardForm.valid;
  }

  protected submit(): void {
    if (!this.isFormValid) {
      this.utilService.openSnackBar('Please select a brand and at least one competitor', { variant: 'error' });
      return;
    }

    this.isLoading.set(true);
    const accountKey = this.stateService.activeAccount();
    this.submitWizard.emit({
      brandKey: this.brandsControl.value!,
      competitorKeys: this.competitorsControl.value,
      deckContent: this.deckContentControl.value.map(c => DECK_CONTENT_TO_SERVER[c]),
      accountKey,
    });
  }

  public stopLoading(): void {
    this.isLoading.set(false);
  }

  protected exit(): void {
    this.isLoading.set(false);
    this.exitWizard.emit();
  }

}
